import { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  ShieldCheck, TrendingUp,
} from 'lucide-react';
import api, { getErrorMessage } from '../../utils/api';

const JOB_LABEL = {
  monthly_rollup_batch: 'Monthly Rollup (1st of month · 00:15 UTC)',
  refund_weekly_digest: 'Refund Weekly Digest (Mon · 8 AM ET)',
  friday_payout_batch: 'Friday Payout Batch',
  charity_receipt_weekly: 'Charity Receipts (Mon)',
  trust_score_snapshot: 'Trust Score Snapshot (daily 02:00 UTC)',
};

const _fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const _statusBadge = (status) => {
  const cfg = {
    ok:               { cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle, label: 'OK' },
    error:            { cls: 'bg-red-100 text-red-800',         icon: XCircle,     label: 'Error' },
    running:          { cls: 'bg-blue-100 text-blue-800',       icon: Clock,       label: 'Running' },
    never_run:        { cls: 'bg-gray-100 text-gray-700',       icon: AlertTriangle, label: 'Never run' },
  }[status] || { cls: 'bg-gray-100 text-gray-700', icon: AlertTriangle, label: status };
  const Icon = cfg.icon;
  return (
    <Badge className={cfg.cls}>
      <Icon className="w-3 h-3 mr-1" />
      {cfg.label}
    </Badge>
  );
};

export default function AdminSystemHealth({ user, onLogout }) {
  const [health, setHealth] = useState(null);
  const [runs, setRuns] = useState([]);
  const [trustScores, setTrustScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingTrust, setRefreshingTrust] = useState(false);

  const _load = useCallback(async () => {
    try {
      setLoading(true);
      const [h, r, s] = await Promise.all([
        api.get('/admin/system-health'),
        api.get('/admin/system-health/runs?limit=50'),
        api.get('/admin/trust-scores'),
      ]);
      setHealth(h.data);
      setRuns(r.data?.runs || []);
      setTrustScores(s.data?.scores || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load system health'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { _load(); }, [_load]);

  const _refreshTrust = async () => {
    setRefreshingTrust(true);
    try {
      const { data } = await api.post('/admin/trust-scores/refresh');
      toast.success(
        `Trust scores refreshed — ${data?.summary?.scored ?? 0} retailers scored.`,
      );
      setTrustScores(data?.scores || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to refresh trust scores'));
    } finally {
      setRefreshingTrust(false);
    }
  };

  const errorCount = (health?.latest_per_job || []).filter((r) => r.status === 'error').length;
  const neverRanCount = (health?.latest_per_job || []).filter((r) => r.status === 'never_run').length;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-system-health-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="w-6 h-6 text-blue-600" />
              System Health
            </h1>
            <p className="text-sm text-gray-600">
              Live status of scheduled jobs. Any red badge means an operator should look.
            </p>
          </div>
          <Button variant="outline" onClick={_load} disabled={loading} data-testid="system-health-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Top-level status cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className={errorCount > 0 ? 'border-red-300' : 'border-emerald-200'}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${errorCount > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                  {errorCount > 0 ? (
                    <XCircle className="w-6 h-6 text-red-600" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <div>
                  <div className="text-sm text-gray-600">Jobs in Error</div>
                  <div className="text-2xl font-bold" data-testid="jobs-error-count">
                    {errorCount}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 p-3 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Jobs Never Run</div>
                  <div className="text-2xl font-bold" data-testid="jobs-never-run-count">
                    {neverRanCount}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Tracked Jobs</div>
                  <div className="text-2xl font-bold" data-testid="jobs-tracked-count">
                    {health?.tracked_jobs?.length ?? 0}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="jobs">
          <TabsList>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Cron Status</TabsTrigger>
            <TabsTrigger value="runs" data-testid="tab-runs">Recent Runs</TabsTrigger>
            <TabsTrigger value="trust" data-testid="tab-trust">Trust Scores</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Latest run per cron</CardTitle>
                <CardDescription>The most recent execution of each tracked scheduled job.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Summary / Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(health?.latest_per_job || []).map((r) => (
                      <TableRow key={r.job_id} data-testid={`job-row-${r.job_id}`}>
                        <TableCell className="text-xs">
                          <div className="font-medium">{JOB_LABEL[r.job_id] || r.job_id}</div>
                          <div className="text-gray-500 font-mono">{r.job_id}</div>
                        </TableCell>
                        <TableCell>{_statusBadge(r.status)}</TableCell>
                        <TableCell className="text-xs">{_fmt(r.started_at)}</TableCell>
                        <TableCell className="text-xs">
                          {r.duration_seconds != null ? `${r.duration_seconds}s` : '—'}
                        </TableCell>
                        <TableCell className="text-xs max-w-md">
                          {r.status === 'error' ? (
                            <span className="text-red-600">{r.error_message}</span>
                          ) : r.summary ? (
                            <pre className="whitespace-pre-wrap text-[11px] text-gray-600">
                              {JSON.stringify(r.summary, null, 0)}
                            </pre>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="runs">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent run log (last 50)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {runs.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500" data-testid="runs-empty">
                    No cron runs recorded yet.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Finished</TableHead>
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runs.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-mono">{r.job_id}</TableCell>
                          <TableCell>{_statusBadge(r.status)}</TableCell>
                          <TableCell className="text-xs">{_fmt(r.started_at)}</TableCell>
                          <TableCell className="text-xs">{_fmt(r.finished_at)}</TableCell>
                          <TableCell className="text-xs">
                            {r.duration_seconds != null ? `${r.duration_seconds}s` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trust">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-purple-600" />
                    Retailer Trust Scores (Phase 1 · Internal Only)
                  </CardTitle>
                  <CardDescription>
                    60% dispute-win rate + 40% evidence-provision speed. Retailers with fewer than 3 resolved disputes are flagged as insufficient data.
                  </CardDescription>
                </div>
                <Button
                  onClick={_refreshTrust}
                  disabled={refreshingTrust}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="trust-refresh-btn"
                >
                  <TrendingUp className={`w-4 h-4 mr-2 ${refreshingTrust ? 'animate-pulse' : ''}`} />
                  {refreshingTrust ? 'Refreshing…' : 'Refresh Snapshot'}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {trustScores.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500" data-testid="trust-empty">
                    No trust scores yet — no retailer has any resolved disputes. Click "Refresh Snapshot" once dispute data exists.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Retailer</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                        <TableHead className="text-right">Win Rate</TableHead>
                        <TableHead className="text-right">Evidence Speed</TableHead>
                        <TableHead className="text-right">Disputes</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Snapshot</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trustScores.map((s, i) => (
                        <TableRow key={s.retailer_id + i} data-testid={`trust-row-${s.retailer_id}`}>
                          <TableCell className="font-mono text-xs">{s.retailer_id?.slice(0, 8)}</TableCell>
                          <TableCell className="text-right font-bold">{s.score?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            {(s.dispute_win_rate * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-right">
                            {s.evidence_median_hours != null
                              ? `${s.evidence_median_hours.toFixed(1)}h`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {s.disputes_approved} / {s.disputes_resolved}
                          </TableCell>
                          <TableCell>
                            {s.insufficient_data && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">
                                Low data
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{_fmt(s.computed_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
